// Rune Tablets (battle design 2.2 and 7.1): ten Tablet Fragments from the Boss Ladder and a coin fee carve the
// tablet of the element you choose at the crafting bench (shared/recipes.ts). Applied to a creature, its Coverage
// slot casts that element's coverage strike instead of its line's: the same power (70) and cooldown, never one of
// the creature's own elements (that would add the same-element bonus), so it is a sideways choice and not a power
// spike. Another tablet changes it again; the tablet of the line's own coverage element puts the slot back.
// The tablet is used up; the creature keeps the swap (CreatureDTO.tablet) through ascension, stars and trades, and
// its rolls never change.
import { ELEMENTS, ELEMENT_IDS, type Element } from './elements.ts';
import { tabletItem } from './items.ts';
import { MOVE, speciesKit } from './moves.ts';
import { SPECIES } from './species.ts';

/** Tablet Fragments per Rune Tablet. */
export const TABLET_FRAGMENTS = 10;
/** Coins burned per Rune Tablet: a sink sized like a Boss Ladder crest's (about 1,000 kills at level 3,000). */
export const TABLET_COINS = 5_000_000;

/** The element a creature's Coverage slot casts now: its tablet's, else its line's own. */
export function coverageElement(c: { species: string; tablet?: string }): Element {
  return MOVE[speciesKit(c.species, c.tablet).coverage].element;
}

/** The element a creature's Coverage slot casts with no tablet. */
export function lineCoverageElement(species: string): Element {
  return MOVE[speciesKit(species).coverage].element;
}

/** True when a Rune Tablet changed this creature's Coverage slot: what a tournament that bans tablets checks. */
export function hasRuneTablet(c: { tablet?: string }): boolean {
  return !!c.tablet;
}

/**
 * Whether a tablet of `el` can go on this creature, and why not. It can't be one of the creature's own elements, nor
 * the element its Coverage slot already casts.
 */
export function tabletCheck(c: { species: string; tablet?: string }, el: string): { ok: boolean; why: string } {
  if (!ELEMENT_IDS.includes(el as Element)) return { ok: false, why: 'No such tablet.' };
  const name = ELEMENTS[el as Element].name;
  if (SPECIES[c.species].elements.includes(el as Element)) return { ok: false, why: `${name} is one of its own elements: its other moves already cast it.` };
  if (coverageElement(c) === el) return { ok: false, why: `Its Coverage move is already ${name}.` };
  return { ok: true, why: '' };
}

/**
 * The creature after a tablet of `el`: the tablet field set, or removed when `el` is the line's own coverage
 * element (the slot is back to how it started, so there is nothing for a tournament to ban).
 */
export function applyTablet<T extends { species: string; tablet?: Element }>(c: T, el: Element): T {
  if (el === lineCoverageElement(c.species)) delete c.tablet;
  else c.tablet = el;
  return c;
}

/** Item id of an element's tablet (re-exported for callers that only know about tablets). */
export { tabletItem };
